import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import Backend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import axiosInstance from './utils/axio-instance';

/**
 * Translations are served from the database (one document per locale).
 * We route the i18next-http-backend `request` through the existing
 * axiosInstance so the DB-backed endpoint reuses the same base URL
 * (REACT_APP_DYNAMO_API_URL) and Authorization header as the rest of
 * the app (see src/api/translationApi.js).
 *
 * If the API request fails (outage, offline), we fall back to the static
 * bundle shipped in /public/locales/{{lng}}/{{ns}}.json so the UI keeps
 * rendering labels.
 */
const loadFromDb = (options, url, payload, callback) => {
  // url is built from backend.loadPath below: "<locale>/<namespace>"
  const [locale, namespace] = url.split('/');

  axiosInstance
    .get(`/translations/${locale}`, { params: { ns: namespace } })
    .then((res) => {
      const messages = res.data?.data ?? res.data ?? {};
      callback(null, { status: 200, data: JSON.stringify(messages) });
    })
    .catch(() => {
      // Fallback to the static bundle bundled with the app.
      fetch(`/locales/${locale}/${namespace}.json`)
        .then((r) => r.text().then((text) => callback(null, { status: r.status, data: text })))
        .catch((err) => callback(err, { status: 0, data: '' }));
    });
};

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    debug: false,
    // Treat every key as a single flat literal — do NOT split on "." or ":".
    // Our keys are stored flat (e.g. "service.buy.home.title"); without this
    // i18next interprets the dots as nesting and t('service') resolves to an
    // object ({ buy: {...} }), which React cannot render
    // ("Objects are not valid as a React child").
    keySeparator: false,
    nsSeparator: false,
    interpolation: {
      escapeValue: false,
    },
    backend: {
      // Passed verbatim to loadFromDb's `url` arg → "<lng>/<ns>".
      loadPath: '{{lng}}/{{ns}}',
      request: loadFromDb,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
    react: {
      useSuspense: false,
    },
  });

export default i18n;
